/* Store data stays in server-rendered cards; Maps is an optional enhancement. */
(() => {
  let mapsLoader;

  function loadMaps(config) {
    if (window.google?.maps?.Map) return Promise.resolve(window.google.maps);
    if (mapsLoader) return mapsLoader;
    mapsLoader = new Promise((resolve, reject) => {
      const timeout = setTimeout(
        () => finish(new Error("Maps timeout")),
        15000,
      );
      const poll = setInterval(() => {
        if (window.google?.maps?.Map) finish();
      }, 100);
      const finish = (error) => {
        clearTimeout(timeout);
        clearInterval(poll);
        if (error) reject(error);
        else resolve(window.google.maps);
      };
      if (window.google?.maps?.importLibrary) {
        Promise.all(
          ["maps", "geocoding", "marker"].map((name) =>
            window.google.maps.importLibrary(name),
          ),
        ).then(() => finish(), finish);
      } else if (
        !document.querySelector(
          'script[src^="https://maps.googleapis.com/maps/api/js"]',
        )
      ) {
        window.__jcmMapsReady = () => finish();
        const query = new URLSearchParams({
          key: config.apiKey,
          v: "weekly",
          loading: "async",
          callback: "__jcmMapsReady",
          libraries: "marker",
        });
        if (config.locale) query.set("language", config.locale);
        const script = document.createElement("script");
        script.src = "https://maps.googleapis.com/maps/api/js?" + query;
        script.async = true;
        script.addEventListener(
          "error",
          () => finish(new Error("Maps unavailable")),
          { once: true },
        );
        document.head.append(script);
      }
    });
    return mapsLoader;
  }

  class StoreLocator extends HTMLElement {
    static parseCoordinates(value) {
      // Standard decimal points separated by comma/semicolon/space; decimal commas use semicolon/space.
      const text = String(value || "")
        .trim()
        .replace(/^\[|\]$/g, "");
      let match = text.match(
        /^(-?\d+(?:\.\d+)?)\s*[,;\s]\s*(-?\d+(?:\.\d+)?)$/,
      );
      if (!match)
        match = text.match(/^(-?\d+(?:,\d+)?)\s*[;\s]\s*(-?\d+(?:,\d+)?)$/);
      if (!match) return null;
      const lat = Number(match[1].replace(",", "."));
      const lng = Number(match[2].replace(",", "."));
      return Number.isFinite(lat) &&
        Number.isFinite(lng) &&
        Math.abs(lat) <= 90 &&
        Math.abs(lng) <= 180
        ? { lat, lng }
        : null;
    }

    static distance(a, b) {
      const rad = (value) => (value * Math.PI) / 180;
      const h =
        Math.sin(rad(b.lat - a.lat) / 2) ** 2 +
        Math.cos(rad(a.lat)) *
          Math.cos(rad(b.lat)) *
          Math.sin(rad(b.lng - a.lng) / 2) ** 2;
      return 6371 * 2 * Math.asin(Math.min(1, Math.sqrt(h)));
    }

    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.config = JSON.parse(this.dataset.config);
      this.cards = [...this.querySelectorAll("[data-store]")].map((card) => ({
        title: card.querySelector("[data-title]").textContent.trim(),
        addressNode: card.querySelector("[data-address]"),
        url: card.dataset.directions,
        coordinates: StoreLocator.parseCoordinates(card.dataset.coordinates),
      }));
      // Use textContent after converting line breaks; never insert merchant data as HTML.
      this.cards.forEach((store) => {
        const copy = store.addressNode.cloneNode(true);
        copy.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
        store.address = copy.textContent;
        delete store.addressNode;
      });
      if (!this.config.enabled || !this.cards.length) return;
      this.status = this.querySelector("[data-status]");
      this.results = this.querySelector("[data-results]");
      this.mapElement = this.querySelector("[data-map]");
      this.searchForm = this.querySelector("[data-search-form]");
      this.geolocateButton = this.querySelector("[data-geolocate]");
      this.busy = true;
      this.searchForm.addEventListener(
        "submit",
        (event) => {
          event.preventDefault();
          if (!this.busy && this.map) this.search();
        },
        { signal: this.controller.signal },
      );
      this.stores = this.cards.filter((store) => store.coordinates);
      this.markers = [];
      this.renderResults(this.stores);
      if (!this.stores.length) {
        this.message(this.config.messages.noCoordinates);
        return;
      }
      if (!this.config.apiKey && !window.google?.maps?.Map) {
        this.message(this.config.messages.unavailable);
        return;
      }
      this.initializeMap();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.infoWindow?.close();
      this.markers?.forEach((marker) => {
        window.google?.maps?.event?.clearInstanceListeners(marker);
        if (marker.setMap) marker.setMap(null);
        else marker.map = null;
      });
      if (this.map)
        window.google?.maps?.event?.clearInstanceListeners(this.map);
      this.controller = null;
      this.map = null;
    }

    message(text) {
      this.status.textContent = text;
    }

    controls(busy) {
      this.busy = busy;
      this.searchForm.querySelector("button").disabled = busy;
      this.geolocateButton.disabled = busy;
    }

    async initializeMap() {
      const lifecycle = this.controller;
      try {
        const maps = await loadMaps(this.config);
        if (lifecycle.signal.aborted) return;
        this.maps = maps;
        this.mapElement.hidden = false;
        this.map = new maps.Map(this.mapElement, {
          center: this.stores[0].coordinates,
          zoom: 10,
          ...(this.config.mapId ? { mapId: this.config.mapId } : {}),
        });
        this.infoWindow = new maps.InfoWindow();
        this.stores.forEach((store) => {
          const marker = this.makeMarker(store.coordinates, store.title);
          marker.addListener("click", () => this.focusStore(store));
          store.marker = marker;
        });
        this.fit(this.stores.map((store) => store.coordinates));
        this.controls(false);
        const options = { signal: lifecycle.signal };
        this.geolocateButton.addEventListener(
          "click",
          () => this.geolocate(),
          options,
        );
      } catch {
        if (!lifecycle.signal.aborted) {
          this.mapElement.hidden = true;
          this.message(this.config.messages.unavailable);
        }
      }
    }

    makeMarker(position, title) {
      const marker =
        this.config.mapId && this.maps.marker?.AdvancedMarkerElement
          ? new this.maps.marker.AdvancedMarkerElement({
              map: this.map,
              position,
              title,
            })
          : new this.maps.Marker({ map: this.map, position, title });
      this.markers.push(marker);
      return marker;
    }

    focusStore(store) {
      if (!this.map) return;
      this.map.panTo(store.coordinates);
      this.map.setZoom(16);
      const content = document.createElement("div");
      content.className = "store-info-window";
      const title = document.createElement("h3");
      title.textContent = store.title;
      const address = document.createElement("p");
      address.textContent = store.address;
      content.append(title, address, this.directionsLink(store));
      this.infoWindow.setContent(content);
      this.infoWindow.open({ anchor: store.marker, map: this.map });
    }

    directionsLink(store) {
      const link = document.createElement("a");
      link.href = store.url;
      link.className = "store-maps-link button-wrapper with-animation";
      const label = document.createElement("span");
      label.className = "button--primary--arrow";
      label.textContent = this.config.messages.directions;
      const arrow = document.createElement("span");
      arrow.className = "button-arrow";
      arrow.setAttribute("aria-hidden", "true");
      const inner = document.createElement("span");
      inner.className = "button-arrow-inner";
      arrow.append(inner);
      link.replaceChildren(label, arrow);
      link.target = "_blank";
      link.rel = "noopener";
      return link;
    }

    renderResults(stores) {
      this.results.replaceChildren();
      const numberFormat = new Intl.NumberFormat(
        this.config.locale || undefined,
        { maximumFractionDigits: 1 },
      );
      stores.forEach((store, index) => {
        const item = document.createElement("div");
        item.className =
          "store-dropdown-item" +
          (typeof store.distance === "number" && index < 3
            ? " nearest-store"
            : "");
        const heading = document.createElement("h3");
        const button = document.createElement("button");
        button.type = "button";
        button.textContent = store.title;
        button.addEventListener("click", () => this.focusStore(store), {
          signal: this.controller.signal,
        });
        heading.append(button);
        const address = document.createElement("p");
        address.textContent = store.address;
        item.append(heading, address);
        if (typeof store.distance === "number") {
          const distance = document.createElement("p");
          distance.textContent = this.config.messages.distance.replace(
            "[distance]",
            numberFormat.format(store.distance),
          );
          item.append(distance);
        }
        item.append(this.directionsLink(store));
        this.results.append(item);
      });
    }

    fit(points) {
      const bounds = new this.maps.LatLngBounds();
      points.forEach((point) => bounds.extend(point));
      this.map.fitBounds(bounds, 50);
      this.maps.event.addListenerOnce(this.map, "idle", () => {
        if (this.map && this.map.getZoom() > 14) this.map.setZoom(14);
      });
    }

    nearest(origin) {
      this.infoWindow.close();
      const stores = this.stores
        .map((store) => ({
          ...store,
          distance: StoreLocator.distance(origin, store.coordinates),
        }))
        .sort((a, b) => a.distance - b.distance);
      this.renderResults(stores);
      if (this.originMarker) {
        if (this.originMarker.setMap) this.originMarker.setMap(null);
        else this.originMarker.map = null;
        this.markers = this.markers.filter(
          (marker) => marker !== this.originMarker,
        );
      }
      this.originMarker = this.makeMarker(
        origin,
        this.config.messages.position,
      );
      this.fit([
        origin,
        ...stores.slice(0, 3).map((store) => store.coordinates),
      ]);
      this.message(this.config.messages.results);
    }

    async search() {
      const address = this.querySelector("[data-location]").value.trim();
      if (!address || !this.map) return;
      const lifecycle = this.controller;
      this.controls(true);
      this.message(this.config.messages.searching);
      let timeout;
      try {
        const request = { address };
        if (/^[a-z]{2}$/i.test(this.config.country || ""))
          request.componentRestrictions = { country: this.config.country };
        const response = await Promise.race([
          new this.maps.Geocoder().geocode(request),
          new Promise((_, reject) => {
            timeout = setTimeout(() => reject(new Error()), 15000);
          }),
        ]);
        if (lifecycle.signal.aborted) return;
        if (!response.results?.length) {
          this.message(this.config.messages.noResults);
          return;
        }
        const point = response.results[0].geometry.location;
        this.nearest({ lat: point.lat(), lng: point.lng() });
      } catch (error) {
        if (!lifecycle.signal.aborted)
          this.message(
            error === "ZERO_RESULTS" || error?.code === "ZERO_RESULTS"
              ? this.config.messages.noResults
              : this.config.messages.unavailable,
          );
      } finally {
        clearTimeout(timeout);
        if (!lifecycle.signal.aborted) this.controls(false);
      }
    }

    geolocate() {
      if (this.busy || !this.map) return;
      if (!navigator.geolocation) {
        this.message(this.config.messages.locationError);
        return;
      }
      const lifecycle = this.controller;
      this.controls(true);
      this.message(this.config.messages.locating);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (lifecycle.signal.aborted) return;
          this.nearest({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          this.controls(false);
        },
        (error) => {
          if (lifecycle.signal.aborted) return;
          this.message(
            error.code === 1
              ? this.config.messages.denied
              : this.config.messages.locationError,
          );
          this.controls(false);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
      );
    }
  }
  if (!customElements.get("jcm-store-locator"))
    customElements.define("jcm-store-locator", StoreLocator);
})();
